import { registerRoute } from '../router';
import { apiFetch, ApiError } from '../api';
import { getUser } from '../state';
import { escapeHtml } from '../format';
import { openModal } from '../components/modal';

type ProviderType = 'twilio_sms' | 'twilio_whatsapp' | 'email' | 'webhook';

interface ProviderSummary {
  id: string;
  type: ProviderType;
  name: string;
  status: 'active' | 'disabled';
  createdAt: string;
}

interface ConfigField {
  key: string;
  label: string;
  type?: string;
  placeholder?: string;
}

const TYPE_LABELS: Record<ProviderType, string> = {
  twilio_sms: 'Twilio — SMS',
  twilio_whatsapp: 'Twilio — WhatsApp',
  email: 'Email',
  webhook: 'Webhook',
};

const CONFIG_FIELDS: Record<ProviderType, ConfigField[]> = {
  twilio_sms: [
    { key: 'accountSid', label: 'Account SID' },
    { key: 'apiKey', label: 'API Key' },
    { key: 'apiSecret', label: 'API Secret', type: 'password' },
    { key: 'fromNumber', label: 'From number', placeholder: '+15551234567' },
  ],
  twilio_whatsapp: [
    { key: 'accountSid', label: 'Account SID' },
    { key: 'apiKey', label: 'API Key' },
    { key: 'apiSecret', label: 'API Secret', type: 'password' },
    { key: 'fromNumber', label: 'From number', placeholder: '+15551234567' },
    { key: 'contentSid', label: 'Default Content Template SID (fallback, optional)', placeholder: 'HXxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx' },
  ],
  email: [
    { key: 'apiKey', label: 'API Key', type: 'password' },
    { key: 'domain', label: 'Domain', placeholder: 'mg.example.com' },
    { key: 'sender', label: 'Sender address', placeholder: 'noreply@example.com' },
  ],
  webhook: [
    { key: 'url', label: 'URL', placeholder: 'https://example.com/hook' },
    { key: 'method', label: 'Method', placeholder: 'POST' },
    { key: 'authHeaderName', label: 'Auth header name (optional)', placeholder: 'Authorization' },
    { key: 'authHeaderValue', label: 'Auth header value (optional)', type: 'password' },
  ],
};

let providers: ProviderSummary[] = [];

async function loadProviders(container: HTMLElement): Promise<void> {
  providers = await apiFetch<ProviderSummary[]>('/api/providers');
  renderList(container);
}

function renderList(container: HTMLElement): void {
  const user = getUser();
  const canManage = user?.role === 'administrator';
  const listEl = container.querySelector('#providers-list')!;

  if (providers.length === 0) {
    listEl.innerHTML = `<p style="color:var(--clr-text-muted);">No providers configured yet.</p>`;
    return;
  }

  listEl.innerHTML = providers
    .map(
      (p) => `
    <div class="card" style="display:flex;align-items:center;justify-content:space-between;margin-bottom:12px;">
      <div>
        <div style="font-weight:600;">${escapeHtml(p.name)}</div>
        <div style="color:var(--clr-text-muted);font-size:0.85rem;">${TYPE_LABELS[p.type]}</div>
      </div>
      <div style="display:flex;align-items:center;gap:10px;">
        <span class="badge ${p.status === 'active' ? 'badge--success' : 'badge--muted'}">${p.status}</span>
        ${
          canManage
            ? `<button class="btn btn--ghost" data-action="edit" data-id="${p.id}">Edit</button>
               <button class="btn btn--ghost" data-action="delete" data-id="${p.id}">Delete</button>`
            : ''
        }
      </div>
    </div>`,
    )
    .join('');

  if (!canManage) return;

  listEl.querySelectorAll<HTMLButtonElement>('[data-action="edit"]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const provider = providers.find((p) => p.id === btn.dataset.id);
      if (provider) openProviderModal(container, provider);
    });
  });

  listEl.querySelectorAll<HTMLButtonElement>('[data-action="delete"]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      if (!confirm('Delete this provider? This cannot be undone.')) return;
      await apiFetch(`/api/providers/${btn.dataset.id}`, { method: 'DELETE' });
      await loadProviders(container);
    });
  });
}

function openProviderModal(container: HTMLElement, existing?: ProviderSummary): void {
  const isEdit = Boolean(existing);
  const typeOptions = Object.entries(TYPE_LABELS)
    .map(([value, label]) => `<option value="${value}" ${existing?.type === value ? 'selected' : ''}>${label}</option>`)
    .join('');

  const { root, close } = openModal(
    isEdit ? 'Edit provider' : 'Add provider',
    `
    <form id="provider-form">
      <div class="field">
        <label for="provider-name">Name</label>
        <input class="input" id="provider-name" required value="${existing ? escapeHtml(existing.name) : ''}">
      </div>
      <div class="field">
        <label for="provider-type">Type</label>
        <select class="input" id="provider-type" ${isEdit ? 'disabled' : ''}>${typeOptions}</select>
      </div>
      <div id="provider-config-fields"></div>
      ${isEdit ? '<p style="color:var(--clr-text-muted);font-size:0.8rem;margin-bottom:14px;">Leave credential fields blank to keep the current ones.</p>' : ''}
      <p class="error-text" id="provider-error"></p>
      <div style="display:flex;gap:10px;justify-content:flex-end;">
        <button type="button" class="btn btn--secondary" id="provider-cancel">Cancel</button>
        <button type="submit" class="btn btn--primary">${isEdit ? 'Save changes' : 'Add provider'}</button>
      </div>
    </form>
  `,
  );

  const typeSelect = root.querySelector<HTMLSelectElement>('#provider-type')!;
  const fieldsContainer = root.querySelector<HTMLElement>('#provider-config-fields')!;

  function renderConfigFields(type: ProviderType): void {
    fieldsContainer.innerHTML = CONFIG_FIELDS[type]
      .map(
        (f) => `
      <div class="field">
        <label for="cfg-${f.key}">${f.label}</label>
        <input class="input" id="cfg-${f.key}" data-key="${f.key}" type="${f.type ?? 'text'}" placeholder="${f.placeholder ?? ''}" ${!isEdit && !f.label.includes('optional') ? 'required' : ''}>
      </div>`,
      )
      .join('');
  }

  renderConfigFields((existing?.type ?? 'twilio_sms') as ProviderType);
  typeSelect.addEventListener('change', () => renderConfigFields(typeSelect.value as ProviderType));

  root.querySelector('#provider-cancel')?.addEventListener('click', close);

  root.querySelector('#provider-form')?.addEventListener('submit', async (event) => {
    event.preventDefault();
    const errorEl = root.querySelector('#provider-error')!;
    errorEl.textContent = '';

    const name = (root.querySelector('#provider-name') as HTMLInputElement).value.trim();
    const type = typeSelect.value as ProviderType;

    const config: Record<string, string> = {};
    let anyFilled = false;
    fieldsContainer.querySelectorAll<HTMLInputElement>('[data-key]').forEach((input) => {
      if (input.value.trim()) {
        config[input.dataset.key!] = input.value.trim();
        anyFilled = true;
      }
    });

    try {
      if (isEdit && existing) {
        const body: { name: string; config?: Record<string, string> } = { name };
        if (anyFilled) body.config = config;
        await apiFetch(`/api/providers/${existing.id}`, { method: 'PATCH', body: JSON.stringify(body) });
      } else {
        await apiFetch('/api/providers', { method: 'POST', body: JSON.stringify({ type, name, config }) });
      }
      close();
      await loadProviders(container);
    } catch (err) {
      errorEl.textContent = err instanceof ApiError ? err.message : 'Something went wrong.';
    }
  });
}

registerRoute({
  path: '/providers',
  title: 'Providers',
  render: ({ container }) => {
    const canManage = getUser()?.role === 'administrator';

    container.innerHTML = `
      <div class="page-header" style="display:flex;align-items:flex-start;justify-content:space-between;gap:16px;">
        <div>
          <h1>Providers</h1>
          <p>Connect the services Connect sends through — credentials stay server-side, never exposed to the browser.</p>
        </div>
        ${canManage ? '<button class="btn btn--primary" id="add-provider-btn">+ Add provider</button>' : ''}
      </div>
      <div id="providers-list">Loading…</div>
    `;

    container.querySelector('#add-provider-btn')?.addEventListener('click', () => openProviderModal(container));

    loadProviders(container).catch(() => {
      container.querySelector('#providers-list')!.textContent = 'Could not load providers.';
    });
  },
});
